
// ✅ src/context/AuthContext.js
import React, { createContext, useState, useEffect, useCallback, useContext, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    login,
    signup,
    googleLogin,
    refreshAuthToken,
    setAuthToken,
    apiRefreshToken,
    logout,
    apiClient,
    getCurrentUser,
} from '../services/api';
import { isPublicPath } from '../constants/routes';
import { capturePreloginSession } from '../utils/capturePreloginSession';
import { clearForeignFroggerKeys, userIdOf } from '../utils/froggerStorageKeys';

export const AuthContext = createContext();

// Debug flag for auth logging
const DEBUG_AUTH = false;

const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [roles, setRoles] = useState([]);
    const [isEmailVerified, setIsEmailVerified] = useState(false);
    const [loading, setLoading] = useState(true);
    const [hydrated, setHydrated] = useState(false);
    const [authLoading, setAuthLoading] = useState(false);
    const [authReady, setAuthReady] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    // Ref to prevent multiple redirects on logout
    const didLogoutRedirectRef = useRef(false);

    const setRefreshToken = (refreshToken) => {
        if (refreshToken) {
            localStorage.setItem('refreshToken', refreshToken);
        } else {
            localStorage.removeItem('refreshToken');
        }
    };

    const refreshUserData = useCallback(async () => {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
            logoutUser();
            return;
        }

        try {
            const response = await apiRefreshToken(refreshToken);
            setAuthToken(response.access);
            setRefreshToken(response.refresh);
        } catch (error) {
            logoutUser();
        }
    }, []);

    const refreshUserProfile = useCallback(async () => {
        if (!localStorage.getItem('authToken')) {
            console.log('[AuthContext] No auth token, skipping profile refresh');
            return;
        }

        try {
            console.log('[AuthContext] Refreshing user profile data...');
            const response = await getCurrentUser();
            
            console.log('[AuthContext] Fresh user data received:', {
                user: response.user,
                isEmailVerified: response.is_email_verified,
                facilityUserRoles: response.facility_user_roles?.length || 0
            });

            // Update state with fresh data
            setUser(response.user);
            setRoles(response.facility_user_roles || []);
            setIsEmailVerified(response.is_email_verified || false);

            // Update localStorage with fresh data
            localStorage.setItem('user', JSON.stringify(response.user));
            localStorage.setItem('facilityUserRoles', JSON.stringify(response.facility_user_roles || []));
            localStorage.setItem('isEmailVerified', response.is_email_verified?.toString() || 'false');
            
            console.log('[AuthContext] Profile refreshed, email verification status:', response.is_email_verified || false);
            return response;
        } catch (error) {
            console.error('[AuthContext] Failed to refresh user profile:', error);
            // Don't logout on profile refresh failure - the user is still authenticated
            return null;
        }
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('authToken');
        const storedUser = localStorage.getItem('user');
        const storedRoles = localStorage.getItem('facilityUserRoles');
        const storedEmailVerified = localStorage.getItem('isEmailVerified');

        if (token && storedUser && storedUser !== 'undefined' && storedUser !== 'null') {
            setAuthToken(token);
            try {
                const user = JSON.parse(storedUser);
                const emailVerified = storedEmailVerified === 'true';

                console.log('[AuthContext] Loading from localStorage:', {
                    user: user,
                    storedEmailVerified: storedEmailVerified,
                    emailVerified: emailVerified
                });

                setUser(user);

                // Guard against invalid JSON in roles
                let roles = [];
                if (storedRoles && storedRoles !== 'undefined' && storedRoles !== 'null') {
                    try {
                        roles = JSON.parse(storedRoles) || [];
                    } catch (e) {
                        console.warn('[AuthContext] Invalid facilityUserRoles in localStorage, defaulting to []');
                        roles = [];
                    }
                }
                setRoles(roles);
                setIsEmailVerified(emailVerified);
            } catch (e) {
                console.error('[AuthContext] Error loading from localStorage:', e);
                localStorage.clear();
                setUser(null);
                setRoles([]);
                setIsEmailVerified(false);
            }
        }

        setLoading(false);
        setHydrated(true);
        setAuthReady(true);
    }, []);

    // R-TRAINING junior Cowork fix 1 (2026-11-04): FroggerAI's browser
    // storage is PER USER. Whenever the signed-in user changes — a
    // sign-in as somebody else, a sign-out, the first hydrate after a
    // reload — every `ff.froggerai.*` key that is not this user's goes.
    // THE ONE PLACE this happens (`utils/froggerStorageKeys.js`). Gated
    // on `hydrated` so the pre-hydrate `null` never wipes the signed-in
    // user's own conversation on a reload.
    const signedInId = userIdOf(user);
    useEffect(() => {
        if (!hydrated) return;
        clearForeignFroggerKeys(signedInId);
    }, [hydrated, signedInId]);

    const loginUser = async (email, password, postLoginCallback, cartItems = []) => {
        // 🔐 CRITICAL: Capture session before login to survive session rotation
        await capturePreloginSession();

        const response = await login(email, password);

        if (response.error === 'google_account') {
            throw new Error("This account was created with Google. Please use Google to log in.");
        }

        if (response.access) {
            console.log('[AuthContext] Login response:', {
                user: response.user,
                isEmailVerified: response.is_email_verified,
                facilityUserRoles: response.facility_user_roles?.length || 0
            });
            
            setAuthToken(response.access);
            setRefreshToken(response.refresh);
            setUser(response.user);
            setRoles(response.facility_user_roles || []);
            setIsEmailVerified(response.is_email_verified || false);

            localStorage.setItem('authToken', response.access);
            localStorage.setItem('refreshToken', response.refresh);
            localStorage.setItem('user', JSON.stringify(response.user));
            localStorage.setItem('facilityUserRoles', JSON.stringify(response.facility_user_roles || []));
            localStorage.setItem('isEmailVerified', response.is_email_verified?.toString() || 'false');
            
            console.log('[AuthContext] Email verification set to:', response.is_email_verified || false);
        }

        if (typeof postLoginCallback === 'function') {
            await postLoginCallback(cartItems);
        }

        return response;
    };

    const signupUser = async (email, password, cartItems = [], postSignupCallback) => {
        setAuthLoading(true);
        try {
            // 🔐 CRITICAL: Capture session before signup to survive session rotation
            await capturePreloginSession();

            const payload = {
                email,
                password1: password,
                password2: password
            };

            const response = await signup(payload);

            if (response.detail?.includes('verification')) {
                console.info("✅ Signup successful — verification required");
            }

            if (typeof postSignupCallback === 'function') {
                try {
                    await postSignupCallback(cartItems);
                } catch (err) {
                    console.warn('postSignupCallback error:', err.message);
                }
            }

            return response;
        } finally {
            setAuthLoading(false);
        }
    };

    const loginWithGoogle = async (googleAuthCode, context = 'login', cartItems = [], postLoginCallback, redirectUri) => {
        try {
          // 🔐 CRITICAL: Capture session before Google login to survive session rotation
          await capturePreloginSession();

          const finalRedirectUri = redirectUri || window.location.origin + (context === 'signup' ? '/signup' : '/login');

          console.log("🔐 [AuthContext] Google login initiated");
          console.log("📤 Context:", context);
          console.log("📤 ID Token:", googleAuthCode?.slice?.(0, 12) + '...');  // Obfuscate for safety
          console.log("📤 Redirect URI:", finalRedirectUri);

          const response = await googleLogin(googleAuthCode, finalRedirectUri);

            if (response.access && response.user) {
                console.log('[AuthContext] Google login response:', {
                    user: response.user,
                    isEmailVerified: response.is_email_verified,
                    facilityUserRoles: response.facility_user_roles?.length || 0
                });
                
                setAuthToken(response.access);
                setRefreshToken(response.refresh);
                setUser(response.user);
                setRoles(response.facility_user_roles || []);
                setIsEmailVerified(response.is_email_verified || false);

                localStorage.setItem('authToken', response.access);
                localStorage.setItem('refreshToken', response.refresh);
                localStorage.setItem('user', JSON.stringify(response.user));
                localStorage.setItem('facilityUserRoles', JSON.stringify(response.facility_user_roles || []));
                localStorage.setItem('isEmailVerified', response.is_email_verified?.toString() || 'false');
                
                console.log('[AuthContext] Google - Email verification set to:', response.is_email_verified || false);
            }

            setHydrated(true);

            if (typeof postLoginCallback === 'function') {
                try {
                    await postLoginCallback(cartItems);
                } catch (err) {
                    console.warn('postLoginCallback error:', err.message);
                }
            }

            return response;
        } catch (err) {
            setAuthToken(null);
            setRefreshToken(null);
            localStorage.clear();
            setUser(null);
            setRoles([]);
            setIsEmailVerified(false);
            setHydrated(true);
            throw err;
        }
    };

    const logoutUser = async () => {
        try {
            await logout();
        } catch (error) {
            console.warn('Error during backend logout:', error.message);
        } finally {
            localStorage.clear();
            // Ensure cartMerged flag is cleared on logout
            localStorage.removeItem('cartMerged');
            setAuthToken(null);
            setRefreshToken(null);
            setUser(null);
            setRoles([]);
            setIsEmailVerified(false);

            // Only redirect if on a protected path
            const currentPath = location.pathname;
            const isPublic = isPublicPath(currentPath);

            if (DEBUG_AUTH) {
                console.log('[AUTH] logout:', {
                    currentPath,
                    isPublic,
                    willRedirect: !isPublic,
                });
            }

            if (!isPublic && !didLogoutRedirectRef.current) {
                didLogoutRedirectRef.current = true;
                if (DEBUG_AUTH) {
                    console.log('[AUTH] redirect -> /login (protected path on logout)', currentPath);
                }
                navigate('/login', { replace: true });
            }
            // If on public path, stay there - no redirect needed
        }
    };

    const isAuthenticated = !!user && !!localStorage.getItem('authToken');

    const waitForAuthReady = useCallback(() => {
        return new Promise((resolve) => {
            if (authReady) {
                resolve();
            } else {
                const checkInterval = setInterval(() => {
                    if (authReady) {
                        clearInterval(checkInterval);
                        resolve();
                    }
                }, 50);
            }
        });
    }, [authReady]);

    return (
        <AuthContext.Provider
            value={{
                user,
                roles,
                isEmailVerified,
                loading,
                hydrated,
                authLoading,
                authReady,
                isAuthenticated,
                waitForAuthReady,
                login: loginUser,
                signup: signupUser,
                loginWithGoogle,
                logout: logoutUser,
                refreshToken: refreshUserData,
                refreshUserProfile,
            }}
        >
            {!loading && hydrated ? children : null}
        </AuthContext.Provider>
    );
};

export default AuthProvider;
export const useAuth = () => useContext(AuthContext);

/**
 * The signed-in user's id, or null — what every FroggerAI storage key is
 * built with. Safe outside a provider (null), so a component renders in
 * a test without one.
 */
export const useSignedInUserId = () => {
    const auth = useContext(AuthContext);
    return userIdOf(auth && auth.user);
};

